import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      // super_admin / admin land on the company dashboard.
      // Store-scoped staff (manager, chef, cashier, employee) land on their own store's overview.
      // Everyone else (customer) goes to their account.
      if (u.role === "super_admin" || u.role === "admin") {
        nav("/admin");
      } else if (["manager", "chef", "cashier", "employee"].includes(u.role)) {
        const home = u.store_id || u.assigned_store_ids?.[0];
        nav(home ? `/admin/stores/${home}` : "/account");
      } else {
        nav("/account");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/v4xitkzq_Sandar%20Sushi%20Store.PNG" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/20"/>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="eyebrow text-white/70 mb-3">Sandar Sushi</p>
          <h2 className="font-display font-bold text-4xl leading-tight">Welcome back.<br/>Fresh sushi awaits.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Sign in</h1>
            <p className="text-sm text-neutral-500 mt-1">New here? <Link to="/register" className="text-[hsl(var(--primary))] font-medium">Create an account</Link></p>
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">{error}</div>}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email"/>
          </div>
          <div>
            <Label htmlFor="pw">Password</Label>
            <div className="relative">
              <Input
                id="pw"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pr-11"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-neutral-500 transition-colors hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Link
              to="/forgot-password"
              className="mt-2 inline-block text-sm font-medium text-[hsl(var(--primary))]"
              data-testid="login-forgot-password"
            >
              Forgot password?
            </Link>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
